import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customFetcher } from '../useFetch';

export interface IDevZugangBenutzer {
  idUser: number;
  Name: string;
  Login: string | null;
  isBlocked: boolean;
  isDev: boolean;
}

export interface IDevZugangListe {
  idUserSelbst: number;
  Benutzer: IDevZugangBenutzer[];
}

/** Testfunktion (z. B. `gwgpruefung`) und die Benutzer, die sie sehen. */
export interface IDevTestfunktion {
  Feature: string;
  Label: string;
  /** Freigeschaltete Benutzer. **Leer bedeutet niemand.** */
  EnabledUserArray: number[];
}

const KEY = ['admin', 'dev', 'benutzer'];
const TESTFUNKTION_KEY = ['admin', 'dev', 'testfunktion'];

/** Benutzer mit Entwickler-Zugang. */
export const useDevZugangListe = () => {
  const { data, isLoading, error } = useQuery<IDevZugangListe>({
    queryKey: KEY,
    queryFn: () => customFetcher({ endpoint: '/admin/dev/benutzer' }),
  });

  return { liste: data, isLoading, error };
};

/** Dev-Zugang für einen Benutzer setzen. */
export const useDevZugangSetzen = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: setzen, isPending } = useMutation({
    mutationFn: ({
      idUser,
      ...body
    }: {
      idUser: number;
      isDev: boolean;
    }): Promise<IDevZugangBenutzer> =>
      customFetcher({
        endpoint: `/admin/dev/benutzer/${idUser}`,
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });

  return { setzen, isPending };
};

/** Wer eine Testfunktion sieht — gepflegt auf ihrer Unterseite unter /admin/dev. */
export const useGetDevTestfunktion = (feature: string) => {
  const { data: testfunktion, isLoading: isLoadingTestfunktion } =
    useQuery<IDevTestfunktion>({
      queryKey: [...TESTFUNKTION_KEY, feature],
      queryFn: () =>
        customFetcher({ endpoint: `/admin/dev/testfunktion/${feature}` }),
    });

  return { testfunktion, isLoadingTestfunktion };
};

/** Ersetzt die Liste der freigeschalteten Benutzer einer Testfunktion. */
export const useSaveDevTestfunktion = (feature: string) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: saveTestfunktion,
    isPending: isPendingSaveTestfunktion,
  } = useMutation({
    mutationFn: (uploadObject: { EnabledUserArray: number[] }) =>
      customFetcher({
        endpoint: `/admin/dev/testfunktion/${feature}`,
        body: uploadObject,
        method: 'PUT',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...TESTFUNKTION_KEY, feature],
      });
    },
  });

  return { saveTestfunktion, isPendingSaveTestfunktion };
};
